import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Typography } from '@mui/material';

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  cameraWrapper: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    backgroundColor: '#000',
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  videoHidden: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'none',
  },
  cameraOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: 1.5,
  },
  scanLineTrack: {
    position: 'absolute',
    inset: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
  },
  cornerTL: {
    position: 'absolute', top: 16, left: 16, width: 36, height: 36,
    borderTop: '3px solid #000', borderLeft: '3px solid #000', borderTopLeftRadius: 6, pointerEvents: 'none',
  },
  cornerTR: {
    position: 'absolute', top: 16, right: 16, width: 36, height: 36,
    borderTop: '3px solid #000', borderRight: '3px solid #000', borderTopRightRadius: 6, pointerEvents: 'none',
  },
  cornerBL: {
    position: 'absolute', bottom: 16, left: 16, width: 36, height: 36,
    borderBottom: '3px solid #000', borderLeft: '3px solid #000', borderBottomLeftRadius: 6, pointerEvents: 'none',
  },
  cornerBR: {
    position: 'absolute', bottom: 16, right: 16, width: 36, height: 36,
    borderBottom: '3px solid #000', borderRight: '3px solid #000', borderBottomRightRadius: 6, pointerEvents: 'none',
  },
  scanHint: {
    position: 'absolute',
    bottom: 20,
    left: '50%',
    transform: 'translateX(-50%)',
    bgcolor: 'rgba(0,0,0,0.5)',
    color: '#fff',
    fontSize: '0.7rem',
    fontWeight: 500,
    px: 1.5,
    py: 0.5,
    borderRadius: 10,
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
  },
  scanLine: {
    height: '2px',
    backgroundImage: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent)',
    boxShadow: '0 0 12px rgba(255,255,255,0.4)',
    width: '100%',
    position: 'absolute',
    left: 0,
    animation: 'scanLine 3s ease-in-out infinite',
    opacity: 0.7,
  },
};

// ── Scan line keyframe (injected once into <head>) ────────────────────────────

const SCAN_LINE_STYLE_ID = 'scanner-modal-keyframes';

function useInjectScanLineKeyframes() {
  React.useEffect(() => {
    if (document.getElementById(SCAN_LINE_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = SCAN_LINE_STYLE_ID;
    style.textContent = `
      @keyframes scanLine {
        0%   { top: 10%; }
        50%  { top: 88%; }
        100% { top: 10%; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById(SCAN_LINE_STYLE_ID);
      if (el) el.remove();
    };
  }, []);
}

// ── CameraFeed ────────────────────────────────────────────────────────────────

export default function CameraFeed({ onBarcodeDetected, active, unfreeze, onSearchingChange }) {
  useInjectScanLineKeyframes();
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const scanFrameRef = useRef(null);
  const onBarcodeDetectedRef = useRef(onBarcodeDetected);
  const scanningRef = useRef(false);
  const [camState, setCamState] = useState('loading');
  const [frozen, setFrozen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [found, setFound] = useState(false);

  // Keep the callback ref current without re-starting the camera
  useEffect(() => { onBarcodeDetectedRef.current = onBarcodeDetected; }, [onBarcodeDetected]);

  useEffect(() => { onSearchingChange?.(searching); }, [searching, onSearchingChange]);

  // Allow parent to unfreeze (e.g. on modal close or tab change)
  useEffect(() => {
    if (unfreeze === 0) return;
    setFrozen(false);
    setSearching(false);
    setFound(false);
    // Restart the rAF loop if it stopped after a successful scan
    if (rafRef.current === null && streamRef.current && scanFrameRef.current) {
      scanningRef.current = false;
      rafRef.current = requestAnimationFrame(scanFrameRef.current);
    }
  }, [unfreeze]);

  const stopCamera = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!active) { stopCamera(); return; }

    let cancelled = false;

    async function start() {
      try {
        // Warm up WASM immediately so first frame has no latency spike
        const { prepareZXingModule, readBarcodes } = await import('zxing-wasm/reader');
        prepareZXingModule({ fireImmediately: true });

        // Let the browser pick the back camera — manual deviceId selection
        // is unreliable across iOS models and browser implementations.
        // facingMode: { ideal: 'environment' } is a soft preference; if no
        // back camera exists the browser falls back to any available camera.
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
        }).catch(() =>
          // Explicit fallback: if environment camera fails entirely, try any camera
          navigator.mediaDevices.getUserMedia({ video: true }),
        );
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        !cancelled && setCamState('active');

        // rAF scan loop — one frame at a time, locked by scanningRef
        async function scanFrame() {
          const video = videoRef.current;
          const canvas = canvasRef.current;
          if (!video || !canvas || video.readyState < 2) {
            rafRef.current = requestAnimationFrame(scanFrame);
            return;
          }

          if (scanningRef.current) {
            rafRef.current = requestAnimationFrame(scanFrame);
            return;
          }

          const { videoWidth: w, videoHeight: h } = video;
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(video, 0, 0, w, h);
          const imageData = ctx.getImageData(0, 0, w, h);

          scanningRef.current = true;
          try {
            const results = await readBarcodes(imageData, {
              formats: ['EAN13', 'EAN8', 'Code128', 'DataMatrix'],
              maxNumberOfSymbols: 1,
              tryHarder: true,
            });
            if (results.length > 0 && results[0].isValid) {
              setFrozen(true);
              setSearching(true);
              try {
                const shouldContinue = await onBarcodeDetectedRef.current(results[0].text);
                if (shouldContinue) {
                  setFrozen(false);
                  setSearching(false);
                  rafRef.current = requestAnimationFrame(scanFrame);
                } else {
                  // Product found — show check, stop spinner
                  setSearching(false);
                  setFound(true);
                  rafRef.current = null;
                }
              } catch (_) {
                // search threw — unfreeze so user can try again
                setFrozen(false);
                setSearching(false);
                setFound(false);
                rafRef.current = requestAnimationFrame(scanFrame);
              }
              return;
            }
          } finally {
            scanningRef.current = false;
          }

          rafRef.current = requestAnimationFrame(scanFrame);
        }

        scanFrameRef.current = scanFrame;
        rafRef.current = requestAnimationFrame(scanFrame);
      } catch (err) {
        if (!cancelled) {
          if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
            setCamState('no-permission');
          } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
            setCamState('no-device');
          } else {
            setCamState('error');
          }
        }
      }
    }

    start();
    return () => { cancelled = true; stopCamera(); };
  }, [active, stopCamera]);

  const messages = {
    loading: t('bruno.camera.loading_scanner', 'Loading camera...'),
    'no-device': t('bruno.camera.no_device', 'No camera found'),
    'no-permission': t('bruno.scanner.permission_blocked', 'Camera blocked by browser. Enable it in your browser settings and reload.'),
    error: t('bruno.camera.no_device', 'Camera unavailable'),
  };

  return (
    <Box sx={styles.cameraWrapper}>
      <video ref={videoRef} style={frozen ? styles.videoHidden : styles.video} muted playsInline />
      {/* Canvas: captures frames for zxing-wasm, visible as frozen frame on detection */}
      <canvas ref={canvasRef} style={frozen ? styles.video : styles.videoHidden} />

      {/* frozen overlay: spinner while searching, check when found */}
      {frozen && searching && (
        <Box sx={{ ...styles.cameraOverlay, bgcolor: 'rgba(0,0,0,0.45)' }}>
          <CircularProgress size={36} sx={{ color: '#fff' }} />
        </Box>
      )}
      {frozen && found && (
        <Box sx={{ ...styles.cameraOverlay, bgcolor: 'rgba(0,0,0,0.45)' }}>
          <span className="material-icons" style={{ fontSize: 64, color: '#4caf50' }}>check_circle</span>
        </Box>
      )}

      {/* inactive states */}
      {camState !== 'active' && (
        <Box sx={styles.cameraOverlay}>
          {camState === 'loading' && <CircularProgress size={36} sx={{ color: '#fff', mb: 1.5 }} />}
          {camState === 'no-permission' && (
            <span className="material-icons" style={{ fontSize: 48, color: '#fff', marginBottom: 8 }}>videocam_off</span>
          )}
          <Typography sx={{ color: '#fff', textAlign: 'center', px: 4, fontSize: '0.85rem', fontWeight: 500, lineHeight: 1.5 }}>
            {messages[camState]}
          </Typography>
        </Box>
      )}

      {/* active overlays */}
      {camState === 'active' && !frozen && (
        <>
          <Box sx={styles.scanLineTrack}>
            <Box sx={styles.scanLine} />
          </Box>
          <Box sx={styles.cornerTL} />
          <Box sx={styles.cornerTR} />
          <Box sx={styles.cornerBL} />
          <Box sx={styles.cornerBR} />
          <Box sx={styles.scanHint}>
            {t('bruno.scanner.align_hint', 'Align barcode within frame')}
          </Box>
        </>
      )}
    </Box>
  );
}
